import React from 'react';
import {
    Table,
    Avatar,
    AvatarSizes,
    AvatarShapes,
    Tooltip,
    EmptyState,
} from '@clockout-dev/design-elements';
import SyncStatusCell from './SyncStatusCell';
import JourneyStageTag from './JourneyStageTag';
import DateTimeCell from './DateTimeCell';
import UserActionsCell from './UserActionsCell';
import PayrollStatusPill from '../../common/payrollStatusPill/PayrollStatusPill';
import { TIMEZONE_NOTE } from '../../../utils/datetime';
import { USERS_TABLE_COPY } from '../reports.constants';
import styles from './styles/reports-table.module.scss';

/** Column key → antd `sortOrder` for the controlled server-side sort. */
function sortOrderFor(sort, key) {
    if (!sort || sort.by !== key) return null;
    return sort.dir === 'asc' ? 'ascend' : 'descend';
}

/**
 * Users report table (Figma node 5693:16478).
 *
 * Columns:
 *  - NAME: `Avatar` with optional email subtitle. Sortable (server-side).
 *  - JOURNEY STAGE: `JourneyStageTag` from `record.journeyStage` — the
 *    funnel-card vocabulary, one label map (`journeyStageTag.constants`).
 *  - PAYROLL: `PayrollStatusPill` from `record.payrollStatus`, the state of
 *    the user's linked payroll connection (KAN-617), coloured from the served
 *    classification; "N connections" suffix when `connectionsCount > 1`,
 *    clicking it opens the user page where each connection is listed. The
 *    Payroll and Sync Status pills on a row describe the SAME connection
 *    (service rule) — the table reconciles nothing.
 *  - SYNC STATUS: `SyncStatusCell` from `record.syncStatus`, the bulk rollup
 *    that arrives with the page — the default render fires no per-row
 *    request. Dimension icons appear once the selected user's detail report
 *    is loaded (`syncDetailByClientId`).
 *  - LAST TRANSACTION: absolute date + time (`DateTimeCell`), explicit
 *    "Never" for `null`; the header states the definition and timezone.
 *    Sortable (server-side; never-transacted rows sort last).
 *  - ACTIONS: `UserActionsCell` (restrict / delete sync / mark complete).
 *    One permission flag per button — they are three different scopes
 *    server-side, so they are three props here.
 *
 * `emptyDescription` lets the screen swap the empty state: the explicit
 * "No users in this card" when a KPI card filter yields zero rows, and the
 * failed copy when the request itself failed (never the empty state).
 *
 * Each row is clickable: triggers `onSelectUser(clientId)` so the side
 * panel shows the detail view. The active row is highlighted via
 * `rowSelected`. Action buttons stop propagation so clicking them does
 * not also fire row selection.
 */
function ReportsUsersTable({
    rows,
    loading,
    pageInfo,
    sort = null,
    onTableChange,
    onSelectUser,
    onViewUser,
    canViewUser = true,
    selectedUserId,
    syncDetailByClientId = {},
    onRetrySyncStatus,
    restrictLoading = {},
    deleteSyncLoading = {},
    markCompleteLoading = {},
    onRestrictUser,
    onDeleteLatestSync,
    onMarkLatestSyncComplete,
    // One reader for "is this member restricted", shared with the detail panel.
    // The table used to inline its own formula; it agreed with the panel's only
    // by accident (see setUserRestricted in reports.delegate.js).
    isRestricted,
    canRestrict = true,
    canDeleteSync = true,
    canMarkSyncComplete = true,
    emptyDescription = USERS_TABLE_COPY.EMPTY,
}) {
    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            sortOrder: sortOrderFor(sort, 'name'),
            showSorterTooltip: false,
            render: (text, record) => (
                <Avatar
                    name={text}
                    subtitle={record.email || undefined}
                    size={AvatarSizes.SMALL}
                    shape={AvatarShapes.ROUNDED}
                />
            ),
        },
        {
            title: (
                <Tooltip title="Where the user sits in the enrollment funnel — the same stages as the cards above and the user page." placement="top">
                    <span>Journey Stage</span>
                </Tooltip>
            ),
            dataIndex: 'journeyStage',
            key: 'journeyStage',
            render: (stage) => <JourneyStageTag stage={stage} />,
        },
        {
            title: (
                <Tooltip title="State of the user's linked payroll connection — the most recently updated one. Users with several connections show a count; open the user page to see each." placement="top">
                    <span>Payroll</span>
                </Tooltip>
            ),
            dataIndex: 'payrollStatus',
            key: 'payrollStatus',
            render: (state, record) => (
                <PayrollStatusPill
                    state={state}
                    connectionsCount={record.connectionsCount}
                    onViewUser={canViewUser && typeof onViewUser === 'function' ? () => onViewUser(record.id) : undefined}
                />
            ),
        },
        {
            title: (
                <Tooltip title="Overall payroll sync state of the user's most recently updated payroll connection — the same connection as the Payroll column. Select a row to see each dimension." placement="top">
                    <span>Sync Status</span>
                </Tooltip>
            ),
            key: 'syncStatus',
            render: (_, record) => (
                <SyncStatusCell
                    rollup={record.syncStatus}
                    report={syncDetailByClientId[record.id]?.status === 'ready' ? syncDetailByClientId[record.id].report : null}
                    onRetry={onRetrySyncStatus}
                />
            ),
        },
        {
            title: (
                <Tooltip title={`Most recent advance, settlement or fee recorded in the ledger. "Never" means the user has no transactions. ${TIMEZONE_NOTE}`} placement="top">
                    <span>Last Transaction</span>
                </Tooltip>
            ),
            dataIndex: 'lastTransactionAt',
            key: 'lastTransactionAt',
            sorter: true,
            sortOrder: sortOrderFor(sort, 'lastTransactionAt'),
            showSorterTooltip: false,
            render: (value) => <DateTimeCell value={value} />,
        },
        {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, record) => (
                <UserActionsCell
                    clientId={record.id}
                    isRestricted={isRestricted(record.id, record)}
                    restrictBusy={!!restrictLoading[record.id]}
                    deleteBusy={!!deleteSyncLoading[record.id]}
                    markBusy={!!markCompleteLoading[record.id]}
                    onRestrictUser={onRestrictUser}
                    onDeleteLatestSync={onDeleteLatestSync}
                    onMarkLatestSyncComplete={onMarkLatestSyncComplete}
                    canRestrict={canRestrict}
                    canDeleteSync={canDeleteSync}
                    canMarkSyncComplete={canMarkSyncComplete}
                />
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={rows}
            pageInfo={pageInfo}
            onChange={onTableChange}
            loading={loading}
            rowKey="id"
            emptyState={<EmptyState title={emptyDescription} />}
            saveSearchParams
            classNames={{ shell: styles.tableShell, root: styles.tableRoot }}
            onRow={(record) => ({
                onClick: () => onSelectUser?.(record.id),
            })}
            rowClassName={(record) =>
                record?.id && record.id === selectedUserId ? styles.rowSelected : ''
            }
        />
    );
}

export default ReportsUsersTable;
